import type { IOption } from '@axo/shared/ui/input/lib/RadioGroup/RadioGroupUI';
import { getFormattedAmount } from '@axo/shared/util/formatters';

const range = (start: number, end: number, increment: number) => {
  const isEndDef = typeof end !== 'undefined';
  end = isEndDef ? end : start;
  start = isEndDef ? start : 0;

  if (typeof increment === 'undefined') {
    increment = Math.sign(end - start);
  }

  const length = Math.abs((end - start) / (increment || 1));

  const { result } = Array.from({ length }).reduce(
    ({ result, current }) => ({
      result: [...result, current],
      current: current + increment,
    }),
    { current: start, result: [] }
  );

  return result;
};

interface IGenerateAmountOptions {
  lowerLimit: number;
  upperLimit: number;
  step: number;
  suffix: string;
}

export const generateAmountOptions = ({
  lowerLimit,
  upperLimit,
  step,
  suffix,
}: IGenerateAmountOptions): IOption[] => {
  // Range is not inclusive so we add a step to get the complete range
  const values = range(lowerLimit, upperLimit + step, step);
  return values.map((value) => ({
    value: String(value),
    text: `${getFormattedAmount(value)} ${suffix}`,
  }));
};

interface IGenerateDurationOptions {
  lowerLimit: number;
  upperLimit: number;
  step: number;
  suffix: string;
}

export const generateDurationOptions = ({
  lowerLimit,
  upperLimit,
  step,
  suffix,
}: IGenerateDurationOptions): IOption[] => {
  // Range is not inclusive so we add a step to get the complete range
  const values = range(lowerLimit, upperLimit + step, step);
  return values.map((value) => ({
    value: String(value),
    text: `${value} ${suffix}`,
  }));
};

type GenerateExpenseOptionsArgs = {
  limit: number;
  step: number;
  suffix: string;
};
export const generateExpensesOptions = ({
  limit,
  step,
  suffix,
}: GenerateExpenseOptionsArgs): IOption[] => {
  const ranges = range(0, limit + step, step);
  const values = ranges.map((value: number) => {
    const lowerValue = value - step > 0 ? value - step : 1;

    const text = value ? `${lowerValue} - ${value} ${suffix}` : 'No expense';
    const result = value ? String(value - step / 2) : '0';

    return {
      text,
      value: result,
    };
  });

  values.push({
    text: `> ${limit} ${suffix}`,
    value: `${limit}`,
  });

  return values;
};
